import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { PropTypes } from "prop-types";
import moment from "moment";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// One series per funnel transition. Colors chosen so spikes pop visually
// and adjacent lines stay distinguishable.
const TRANSITIONS = [
  { from: "step0", to: "step0_5", label: "0 → 0.5 (no contact)", color: "#f59e0b" },
  { from: "step0_5", to: "step1", label: "0.5 → 1 (no form)", color: "#10b981" },
  { from: "step1", to: "step2", label: "1 → 2 (no slot)", color: "#3b82f6" },
  { from: "step2", to: "step3", label: "2 → 3 (no review)", color: "#8b5cf6" },
  { from: "step3", to: "completed", label: "3 → done (no confirm)", color: "#ef4444" },
];

function dropOff(from, to) {
  if (!from) return null;
  return ((from - to) / from) * 100;
}

function DropOffTrendChart({ days, loading, error }) {
  const data = useMemo(() => {
    const safe = Array.isArray(days) ? days : [];
    const labels = safe.map((d) => moment(d.date, "YYYYMMDD").format("MMM D"));
    const datasets = TRANSITIONS.map((t) => ({
      label: t.label,
      data: safe.map((d) => dropOff(d[t.from], d[t.to])),
      borderColor: t.color,
      backgroundColor: t.color,
      tension: 0.3,
      spanGaps: true, // skip days with no baseline rather than dropping to 0
      pointRadius: 3,
      pointHoverRadius: 6,
    }));
    return { labels, datasets };
  }, [days]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      title: { display: false },
      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ctx.parsed.y == null
              ? `${ctx.dataset.label}: n/a`
              : `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { callback: (v) => `${v}%` },
        title: { display: true, text: "Drop-off (%)" },
      },
    },
  };

  if (loading) {
    return (
      <div className="lb-chart-state">
        <FontAwesomeIcon spin icon={faSpinner} style={{ fontSize: 22 }} />
        <span>Loading drop-off trend…</span>
      </div>
    );
  }

  if (error) {
    return <div className="lb-chart-state">Couldn’t load drop-off trend: {error}</div>;
  }

  if (!days || days.length === 0) {
    return <div className="lb-chart-state">No data available for this range.</div>;
  }

  return (
    <div className="lb-chart lb-chart--multiline">
      <Line options={options} data={data} />
    </div>
  );
}

DropOffTrendChart.propTypes = {
  days: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default DropOffTrendChart;
